import store from '@/store';
import { getUserByPhoneNumber } from '@/handlers/apis';
// import AisService from '@/services/aisService';
import { environmentConfig, serviceIdToPackageMap } from '@/config/enviromentConfig';
import { Brands } from '@/brand/brandConfig';
import DepService from '@/services/depService';
import WorldPlayService from '@/services/worldPlayService';

const WORLDPLAY_DEP_URL = 'http://doi.mtndep.co.za/service/11010';
const WP_HE_ATTEMPTED_KEY = 'wpHeaderEnrichmentAttempted';

function redirectToWorldPlayDep() {
  return window.open(WORLDPLAY_DEP_URL, '_self');
}

export async function userAuthenticate(to, from, next) {
  const { brand } = store.state.brandConfig;
  const { productId } = store.state.brandConfig;
  switch (brand) {
    case Brands.MTNNigeriaEn: {
      return userAuthenticateNG(to, from, next, productId);
    }
    case Brands.UpstreamAISTh: {
      return await userAuthenticateAis(to, from, next, productId);
    }
    case Brands.DemoEn: {
      return await userAuthenticateAis(to, from, next, productId);
    }
    case Brands.MTNWorldPlaySouthAfticaEnglish: {
      return await userAuthenticateWorldPlayHeaderEnrichment(to, from, next, productId);
    }
    case Brands.TelkomWorldPlaySouthAfticaEnglish: {
      return await userAuthenticateWorldPlay(to, from, next, productId);
    }
    case Brands.WorldPlaySouthAfticaEnglish: {
      return await userAuthenticateWorldPlayHeaderEnrichment(to, from, next, productId);
    }
    default:
      return await userAuthenticateAis(to, from, next, productId);
  }
}

async function userAuthenticateWorldPlay(to, from, next, productId) {
  if (store.state.user) return next();

  const allowedRoutes = ['termsConditions', 'msisdn', 'wifi'];
  if (allowedRoutes.includes(to.name)) return next();

  const urlParams = new URLSearchParams(window.location.search);
  const msisdn = sanitizeMsisdn(urlParams.get('tn'))
    || sanitizeMsisdn(urlParams.get('msisdn'))
    || sanitizeMsisdn(localStorage.getItem(`msisdn`));

  if (!msisdn) {
    return next({ name: 'msisdn' });
  }

  return loginWorldPlayUser(msisdn, productId, next);
}

async function userAuthenticateWorldPlayHeaderEnrichment(to, from, next, productId) {
  if (store.state.user) return next();

  const allowedRoutes = ['termsConditions', 'msisdn', 'wifi'];
  if (allowedRoutes.includes(to.name)) return next();

  // Persist debug flag across the HE redirect (flag comes from the initial game URL).
  isHeaderEnrichmentDebugEnabled();

  const urlParams = new URLSearchParams(window.location.search);
  const msisdn = await resolveWorldPlayMsisdn(urlParams);
  if (!msisdn) {
    if (sessionStorage.getItem(WP_HE_ATTEMPTED_KEY)) {
      return next({ name: 'msisdn' });
    }
    sessionStorage.setItem(WP_HE_ATTEMPTED_KEY, 'true');
    window.open(getMtnHeaderEnrichmentUrl(urlParams), '_self');
    return next();
  }

  sessionStorage.removeItem(WP_HE_ATTEMPTED_KEY);
  return loginWorldPlayUser(msisdn, productId, next);
}

async function loginWorldPlayUser(msisdn, productId, next) {
  localStorage.setItem(`msisdn`, msisdn);
  let getUser;
  try {
    getUser = await WorldPlayService.getUser(msisdn, productId);
  } catch (error) {
    console.log('WorldPlay getUser failed', error);
    return redirectToWorldPlayDep();
  }
  const userData = {
    ...getUser,
    msisdn: getUser.Msisdn,
  };

  const userStatus = getUser.Status?.toLowerCase();
  const validUser = userStatus === 'pending' || userStatus === 'active';
  if (validUser) {
    const mmrData = await getUserByPhoneNumber(userData.msisdn);
    store.state.user = (Object.assign(userData, mmrData));
    localStorage.setItem('userData', JSON.stringify(userData));
    await DepService.trackUserLogin(msisdn, productId);
    return next({ name: 'home' });
  } else {
    return redirectToWorldPlayDep();
  }
}

async function resolveWorldPlayMsisdn(urlParams) {
  const wpMsisdn = sanitizeMsisdn(urlParams.get('tn'));
  if (wpMsisdn) {
    debugWorldPlayMsisdn('tn', wpMsisdn);
    return wpMsisdn;
  }

  const urlMsisdn = sanitizeMsisdn(urlParams.get('msisdn'));
  if (urlMsisdn) {
    debugWorldPlayMsisdn('msisdn', urlMsisdn);
    return urlMsisdn;
  }

  const encryptedMsisdn = urlParams.get('e-msisdn');
  if (encryptedMsisdn) {
    try {
      const { rawMsisdn } = await DepService.decryptMsisdnV2(encryptedMsisdn);
      const decryptedMsisdn = sanitizeMsisdn(rawMsisdn);
      if (decryptedMsisdn) {
        debugWorldPlayMsisdn('e-msisdn', decryptedMsisdn);
        return decryptedMsisdn;
      }
    } catch (error) {
      console.log('Failed to decrypt e-msisdn', error);
    }
  }

  const storedMsisdn = sanitizeMsisdn(localStorage.getItem(`msisdn`));
  if (storedMsisdn) {
    debugWorldPlayMsisdn('localStorage', storedMsisdn);
  }

  return storedMsisdn;
}

function sanitizeMsisdn(msisdn) {
  if (!msisdn || msisdn === 'null' || msisdn === 'undefined') return undefined;
  return msisdn;
}

function debugWorldPlayMsisdn(source, msisdn) {
  if (!isHeaderEnrichmentDebugEnabled()) return;
  const message = `[HE TEST] MSISDN resolved from ${source}: ${msisdn}`;
  console.log(message);
  window.alert(message);
}

function isHeaderEnrichmentDebugEnabled() {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const dev = urlParams.get('dev') || urlParams.get('he_debug') || urlParams.get('heDebug');

    if (dev !== null) {
      // Treat `dev=0` as disable, anything else as enable.
      localStorage.setItem('he_debug', dev === '0' ? 'false' : 'true');
    }

    return localStorage.getItem('he_debug') === 'true';
  } catch (e) {
    // If localStorage is unavailable for any reason, just don't alert.
    return false;
  }
}

function getMtnHeaderEnrichmentUrl(urlParams = new URLSearchParams(window.location.search)) {
  const redirectUrl = new URL(environmentConfig.mtnHeaderEnrichmentUrl);
  redirectUrl.searchParams.set('game', 'mega-match-rivals');

  ['cid', 'gclid', 'clickId'].forEach((paramName) => {
    const paramValue = urlParams.get(paramName);
    if (paramValue) {
      redirectUrl.searchParams.set(paramName, paramValue);
    }
  });

  return redirectUrl.toString();
}

async function userAuthenticateNG(to, from, next, productId) {
  if (from.fullPath === '/' && to.path === `/login`) {
    const userData = setNGUser();
    const msisdn = userData.msisdn;
    return await allowUserAccessNG(userData, productId) ? next({
      name: 'home',
      query: { msisdn },
    }) : next({ name: 'error' });
  }
  if (from.fullPath === '/') return next();
  if (store.state.user) return next();
  if (to.name === 'signup' || to.name === 'error' || to.name === 'termsConditions') return next();
}

async function allowUserAccessNG(userData, productId) {
  const { user } = await DepService.getUser(productId, userData.msisdn);
  const statusName = String(user?.statusName || '').toUpperCase();
  const validUser = statusName === 'ACTIVE' || statusName === 'CONFIRMED';
  if (validUser) {
    const mmrData = await getUserByPhoneNumber(userData.msisdn);
    store.state.user = (Object.assign(user, mmrData));
    localStorage.setItem('userData', JSON.stringify(user));
  }
  return validUser;
}

function setNGUser() {
  const urlParams = new URLSearchParams(window.location.search);
  const userData = {
    channel: urlParams.get('channel'),
    extRef: urlParams.get('ext_ref'),
    msisdn: urlParams.get('msisdn'),
    result: urlParams.get('result'),
    resultId: urlParams.get('result_id'),
    subscriptionId: urlParams.get('subscription_id'),
    serviceId: urlParams.get('service_id'),
    package: getPackageDetails(urlParams.get('service_id')),
  };

  localStorage.setItem('userData', JSON.stringify(userData));
  return userData;
}

function getPackageDetails(serviceId) {
  if (serviceIdToPackageMap.has(serviceId?.toString())) {
    return serviceIdToPackageMap.get(serviceId);
  } else return serviceIdToPackageMap.get(`default`);
}

async function userAuthenticateAis(to, from, next) {
  return next();
  // console.log("🚀 ~ userAuthenticateAis ~ next:", next)
  // console.log("🚀 ~ userAuthenticateAis ~ from:", from)
  // console.log("🚀 ~ userAuthenticateAis ~ to:", to)
  // if (from.fullPath === '/') return next();
  // console.log("Coming here")
  // if (store.state.user) return next();
  // console.log("Coming there")

  // if (to.name === 'signup' || to.name === 'error' || to.name === 'termsConditions') return next();

  // const urlParams = new URLSearchParams(window.location.search);
  // console.log("🚀 ~ userAuthenticateAis ~ msisdn:", urlParams)
  // const msisdn = urlParams.get('msisdn') || store.state.user.msisdn;
  // console.log("🚀 ~ userAuthenticateAis ~ msisdn:", msisdn)

  // if (msisdn) return await msisdnLoginAuth(msisdn, urlParams, next);
}

// async function msisdnLoginAuth(msisdn, urlParams, next) {
//     await setUserData(msisdn, urlParams);
//     return redirectBasedOnUserStatus(store.state.user, msisdn, next);
// }

// async function setUserData(msisdn, urlParams) {
//     const user = await AisService.getUserViaProductId(store.state.brandConfig.productId, msisdn);
//     const mmrUser = await getUserByPhoneNumber(msisdn);

//     const userData = {
//         channel: urlParams.get('channel'),
//         extRef: urlParams.get('ext_ref'),
//         msisdn: msisdn,
//         result: urlParams.get('result'),
//         resultId: urlParams.get('result_id'),
//         subscriptionId: urlParams.get('subscription_id'),
//     };

//     localStorage.setItem('userData', JSON.stringify(user));
//     store.commit('setUser', Object.assign(userData, mmrUser));
//     store.commit('setMsisdn', msisdn);
// }


// function redirectBasedOnUserStatus(userData, msisdn, next) {
//     return isUserResultValid(userData.result) ? next({name: 'home', query: {msisdn}}) : next({name: 'error'});
// }

// function isUserResultValid(userResult) {
//     return userResult === 'ACTIVE' || userResult === 'CONFIRMED';
// }


